import { globalConfirm } from '../../../components/ConfirmDialog';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LoadingSpinner, EmptyState } from '../../../components/layout/SharedComponents';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { useExpenditureLabels, useDecimalFormat } from '../../../contexts/ExpenditureLabelsContext';
import DataLookupField from '../../../components/DataLookupField';
import DragSortList from '../../../components/DragSortList';
import TypeAheadLookup from '../../../components/TypeAheadLookup';
import {
  Plus, Edit, Trash2, Save, Search, Download, X,
  FileText, Users, ChevronRight, ChevronDown, Bell,
  Lock, Eye, Pencil, Settings, Package
} from 'lucide-react';
import { SortableTableHeader, useSortState, sortData } from '../../../components/SortableTableHeader';
import { useAuth } from '../../../contexts/AuthContext';

export function PlaceholderPanel({ title }) {
  return (
    <div><h1 className="text-2xl font-bold mb-1">System Approval</h1>
      <p className="text-lg font-medium text-muted-foreground mb-4">{title}</p>
      <div className="border rounded-lg p-8 text-center text-muted-foreground">Coming soon</div>
    </div>
  );
}

export function CostCentreApprovalPanel() {
  const { fmtInput } = useDecimalFormat();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings(); }, []);
  const fetchSettings = async () => {
    try { const r = await api.get('/api/admin/approval-settings'); setSettings(r.data.settings || {}); } catch {}
    setLoading(false);
  };
  const u = (k, v) => setSettings(p => ({ ...p, [k]: v }));
  const handleSave = async () => {
    setSaving(true);
    try {
      const { _id, id, type, ...payload } = settings;
      // Strip commas from formatted numbers before sending
      if (typeof payload.cost_centre_approval_limit === 'string') payload.cost_centre_approval_limit = parseFloat(payload.cost_centre_approval_limit.replace(/,/g, '')) || 0;
      await api.put('/api/admin/approval-settings', payload);
      toast.success('Approval settings saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  if (loading || !settings) return <LoadingSpinner />;
  return (
    <div data-testid="cost-centre-approval-panel">
      <h1 className="text-2xl font-bold mb-1">System Approval</h1>
      <p className="text-lg font-medium text-muted-foreground mb-4">Cost Centre Approval</p>
      <p className="text-sm text-muted-foreground mb-6 border-b pb-4">
        Before processing into a Purchase Order, you can insist that any Order, which exceeds a pre-set limit, requires a formal sign-off by one or more nominated approvers.
        In addition, you can set a filter so that the approvers may only sign-off for a specific Cost Centre to the value of a pre-set limit.
      </p>
      <div className="grid grid-cols-2 gap-8 mb-6">
        {/* Left: Activate + Limit */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Switch checked={settings.cost_centre_approval_active} onCheckedChange={v => u('cost_centre_approval_active', v)} />
            <Label className="text-sm font-semibold">Activate Cost Centre Approval</Label>
          </div>
          {settings.cost_centre_approval_active && (
            <div className="space-y-1">
              <Label className="text-xs">Cost Centre Approval Limit (USD)</Label>
              <Input type="text" value={typeof settings.cost_centre_approval_limit === 'string' && settings.cost_centre_approval_limit.includes(',') ? settings.cost_centre_approval_limit : Number(settings.cost_centre_approval_limit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                onChange={e => u('cost_centre_approval_limit', e.target.value.replace(/,/g, ''))}
                onBlur={() => u('cost_centre_approval_limit', Number(String(settings.cost_centre_approval_limit).replace(/,/g, '') || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))}
                className="w-48 text-right" />
              <p className="text-xs text-muted-foreground">(Threshold above which approval is mandatory)</p>
            </div>
          )}
        </div>
        {/* Right: Approval Options */}
        {settings.cost_centre_approval_active && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 mb-1">
              <Label className="text-sm font-semibold">Select Approval Option</Label>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Define approval work flow</p>
            {[
              { val: 1, title: 'Option 1:', sub: 'Skip over Subordinates and allow Final Approver only', desc: "The approvers whose approval limit matches or is higher than the cost centre split value will be selected." },
              { val: 2, title: 'Option 2:', sub: 'Allows the Final Approver subordinates to also sign', desc: "All the approvers of the selected cost centre will get populated regardless of the approval limit. Approvers with the same approval limits will show alphabetically." },
              { val: 3, title: 'Option 3:', sub: 'Allow all Subordinates and a single higher-level financial Authority', desc: "All approvers whose limit is lower than the split value and one Approver whose limit matches or is closest to the split value, on the higher side, will be populated." },
            ].map(opt => (
              <label key={opt.val} className={`block border rounded-lg p-4 cursor-pointer transition-all ${settings.cost_centre_approval_option === opt.val ? 'border-primary bg-primary/5' : 'border-muted hover:border-primary/30'}`}>
                <div className="flex items-start gap-3">
                  <input type="radio" name="cc_option" value={opt.val} checked={settings.cost_centre_approval_option === opt.val}
                    onChange={() => u('cost_centre_approval_option', opt.val)} className="h-4 w-4 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold">{opt.title}</p>
                    <p className="text-xs">{opt.sub}</p>
                    <p className="text-xs text-muted-foreground mt-1">{opt.desc}</p>
                  </div>
                </div>
              </label>
            ))}
            <div className="flex items-center gap-3 mt-4 pt-4 border-t">
              <Switch checked={settings.populate_cost_centre_approvers} onCheckedChange={v => u('populate_cost_centre_approvers', v)} />
              <span className="text-sm font-medium">Populate Cost Centre Approver(s)</span>
            </div>
            <p className="text-xs text-muted-foreground">When this function is checked, then the relevant Approvers will get automatically populated. If it is unchecked, then users will need to select the approvers manually.</p>
          </div>
        )}
      </div>
      <div className="flex justify-end"><Button onClick={handleSave} disabled={saving}><Save className="h-4 w-4 mr-1" />{saving ? 'Saving...' : 'Save'}</Button></div>
    </div>
  );
}

export function SetCCDefaultApproverPanel() {
  const [data, setData] = useState({ cost_centres: [], eligible_users: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/api/admin/cost-centre-default-approvers', { params: { search } }); setData(r.data); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const setApprover = async (code, name) => {
    try {
      await api.put(`/api/admin/cost-centre-default-approvers/${encodeURIComponent(code)}`, { approver_name: name });
      setData(prev => ({ ...prev, cost_centres: prev.cost_centres.map(c => c.code === code ? { ...c, default_approver: name } : c) }));
      toast.success(`Default approver set for ${code}`);
    } catch { toast.error('Failed to set approver'); }
  };

  if (loading) return <LoadingSpinner />;
  return (
    <div data-testid="set-cc-default-approver-panel">
      <h1 className="text-2xl font-bold mb-1">System Approval</h1>
      <p className="text-lg font-medium text-muted-foreground mb-4">Set Cost Centre Default Approver</p>
      <p className="text-sm text-muted-foreground mb-4">You can select a Default Approver for each Cost Centre that will be selected automatically when you use that Cost Centre on an Order.</p>
      <div className="flex items-center gap-2 mb-4">
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search" className="w-40" />
        <Button size="sm" onClick={load}><Search className="h-3 w-3 mr-1" /> Search</Button>
        <Button size="sm" variant="outline" onClick={() => { setSearch(''); setTimeout(load, 100); }}><X className="h-3 w-3 mr-1" /> Clear</Button>
        <div className="flex-1" />
        <span className="text-xs text-muted-foreground">Total Record: {data.cost_centres.length}</span>
      </div>
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr><SortableTableHeader label="Cost Centre Code" field="code" sortState={ccSort} onSort={handleCcSort} /><SortableTableHeader label="Description" field="description" sortState={ccSort} onSort={handleCcSort} /><SortableTableHeader label="Default Approver" field="default_approver" sortState={ccSort} onSort={handleCcSort} /><th className="w-10"></th></tr>
          </thead>
          <tbody>
            {sortData(data.cost_centres, ccSort.field, ccSort.dir).map(c => (
              <tr key={c.code} className="border-t hover:bg-accent/30">
                <td className="px-3 py-2 font-mono">{c.code}</td>
                <td className="px-3 py-2">{c.description}</td>
                <td className="px-3 py-2">{c.default_approver || ''}</td>
                <td className="px-2">
                  <select value={c.default_approver || ''} onChange={e => setApprover(c.code, e.target.value)}
                    className="h-7 w-40 rounded border border-input bg-background px-2 text-xs">
                    <option value="">— None —</option>
                    {data.eligible_users.map(u => <option key={u.user_id} value={u.name}>{u.name}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function GroupApprovalPanel() {
  const { fmtInput, decimalPlaces } = useDecimalFormat();
  const [settings, setSettings] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newGroup, setNewGroup] = useState({ group_code: '', description: '', group_mandatory: false, active: true, routing_method: 'any_one', members: [] });
  const [editingGroup, setEditingGroup] = useState(null);
  const [allUsers, setAllUsers] = useState([]);

  useEffect(() => { fetchAll(); }, []);
  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sRes, gRes, uRes] = await Promise.all([api.get('/api/admin/approval-settings'), api.get('/api/admin/approval-groups'), api.get('/api/admin/users?limit=200')]);
      setSettings(sRes.data.settings || {});
      setGroups(gRes.data.groups || []);
      setAllUsers((uRes.data.users || []).map(u => ({ user_id: u.user_id, name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email })));
    } catch {}
    setLoading(false);
  };
  const u = (k, v) => setSettings(p => ({ ...p, [k]: v }));
  const handleSave = async () => {
    setSaving(true);
    try {
      const { _id, id, type, ...payload } = settings;
      if (typeof payload.group_approval_limit === 'string') payload.group_approval_limit = parseFloat(payload.group_approval_limit.replace(/,/g, '')) || 0;
      await api.put('/api/admin/approval-settings', payload);
      toast.success('Group approval settings saved');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };
  const handleCreateGroup = async () => {
    if (!newGroup.group_code) { toast.error('Group code required'); return; }
    try { await api.post('/api/admin/approval-groups', newGroup); toast.success('Group created'); setNewGroup({ group_code: '', description: '', group_mandatory: false, active: true }); setShowAdd(false); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Create failed'); }
  };
  const handleDeleteGroup = async (code) => {
    if (!await globalConfirm('Delete this group?')) return;
    try { await api.delete(`/api/admin/approval-groups/${code}`); toast.success('Group deleted'); fetchAll(); }
    catch { toast.error('Delete failed'); }
  };
  const toggleGroupField = async (code, field, value) => {
    try { await api.put(`/api/admin/approval-groups/${code}`, { [field]: value }); setGroups(prev => prev.map(g => g.group_code === code ? { ...g, [field]: value } : g)); }
    catch { toast.error('Update failed'); }
  };

  if (loading || !settings) return <LoadingSpinner />;
  return (
    <div data-testid="group-approval-panel">
      <h1 className="text-2xl font-bold mb-1">System Approval</h1>
      <p className="text-lg font-medium text-muted-foreground mb-4">Group Approval</p>
      <p className="text-sm text-muted-foreground mb-4 border-b pb-4">Before processing into a Purchase Order, you can insist that any Order, which exceeds a pre-set limit, requires a formal sign-off by one or more nominated Approval Groups.</p>

      {/* Group Approval toggle + limit */}
      <div className="space-y-4 mb-6">
        <div className="flex items-center gap-3">
          <Label className="w-48 text-sm">Activate Group Approval</Label>
          <Switch checked={settings.group_approval_active} onCheckedChange={v => u('group_approval_active', v)} />
          <span className={`text-sm font-medium ${settings.group_approval_active ? 'text-green-600' : ''}`}>{settings.group_approval_active ? 'Yes' : 'No'}</span>
        </div>
        {settings.group_approval_active && (
          <div className="flex items-start gap-4">
            <Label className="w-48 text-sm pt-2">Group Approval Limit (USD)</Label>
            <div>
              <Input type="text" value={typeof settings.group_approval_limit === 'string' && settings.group_approval_limit.includes(',') ? settings.group_approval_limit : Number(settings.group_approval_limit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                onChange={e => u('group_approval_limit', e.target.value.replace(/,/g, ''))}
                onBlur={() => u('group_approval_limit', Number(String(settings.group_approval_limit).replace(/,/g, '') || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))}
                className="w-48 text-right" />
              <p className="text-xs text-muted-foreground mt-1">Threshold Above Which Approval Is Mandatory</p>
            </div>
          </div>
        )}
        <div className="flex justify-end"><Button size="sm" onClick={handleSave} disabled={saving}><Save className="h-4 w-4 mr-1" />{saving ? 'Saving...' : 'Save'}</Button></div>
      </div>

      {/* Manage Group Approval — CRUD table */}
      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-sm">Manage Group Approval</h3>
          <Button size="sm" variant="outline" onClick={() => setShowAdd(true)}><Plus className="h-3 w-3 mr-1" /> Add New</Button>
        </div>
        {showAdd && (
          <div className="border rounded-lg p-4 mb-4 grid grid-cols-4 gap-3 items-end bg-muted/10">
            <div className="space-y-1"><Label className="text-xs">Group Code *</Label><Input value={newGroup.group_code} onChange={e => setNewGroup(p => ({ ...p, group_code: e.target.value }))} /></div>
            <div className="space-y-1"><Label className="text-xs">Description</Label><Input value={newGroup.description} onChange={e => setNewGroup(p => ({ ...p, description: e.target.value }))} /></div>
            <div className="flex items-center gap-2 pt-4"><Switch checked={newGroup.group_mandatory} onCheckedChange={v => setNewGroup(p => ({ ...p, group_mandatory: v }))} /><Label className="text-xs">Mandatory</Label></div>
            <div className="flex gap-2"><Button size="sm" onClick={handleCreateGroup}>Create</Button><Button size="sm" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button></div>
          </div>
        )}
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr><SortableTableHeader label="Group Code" field="group_code" sortState={groupSort} onSort={handleGroupSort} /><SortableTableHeader label="Description" field="description" sortState={groupSort} onSort={handleGroupSort} /><SortableTableHeader label="Routing" field="approval_route" sortState={groupSort} onSort={handleGroupSort} className="text-center" /><SortableTableHeader label="Members" field="member_count" sortState={groupSort} onSort={handleGroupSort} className="text-center" /><SortableTableHeader label="Mandatory" field="mandatory_all" sortState={groupSort} onSort={handleGroupSort} className="text-center" /><SortableTableHeader label="Active" field="active" sortState={groupSort} onSort={handleGroupSort} className="text-center" /><th className="w-10"></th></tr>
            </thead>
            <tbody>
              {groups.length === 0 ? <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground text-xs">No approval groups</td></tr> :
                sortData(groups, groupSort.field, groupSort.dir).map(g => (
                  <tr key={g.group_code} className="border-t hover:bg-accent/30 cursor-pointer" onClick={() => setEditingGroup({...g})}>
                    <td className="px-3 py-2">{g.group_code}</td>
                    <td className="px-3 py-2">{g.description}</td>
                    <td className="px-3 py-2 text-center">
                      <Badge variant="outline" className="text-[10px]">
                        {g.routing_method === 'all_at_once' ? 'All Must Approve' : g.routing_method === 'sequential' ? 'Sequential' : 'Any One'}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-center">{(g.members || []).length}</td>
                    <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}><Switch checked={g.group_mandatory} onCheckedChange={v => toggleGroupField(g.group_code, 'group_mandatory', v)} /></td>
                    <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}><Switch checked={g.active !== false} onCheckedChange={v => toggleGroupField(g.group_code, 'active', v)} /></td>
                    <td className="px-2" onClick={e => e.stopPropagation()}><Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeleteGroup(g.group_code)}><Trash2 className="h-3 w-3" /></Button></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Group Editor Dialog */}
      {editingGroup && (
        <Dialog open onOpenChange={() => setEditingGroup(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Edit Group: {editingGroup.group_code}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label className="text-xs">Description</Label>
                <Input value={editingGroup.description || ''} onChange={e => setEditingGroup(p => ({...p, description: e.target.value}))} className="h-8 text-sm" /></div>
              <div><Label className="text-xs">Routing Method</Label>
                <div className="flex gap-2 mt-1">
                  {[['any_one', 'Any One'], ['all_at_once', 'All Must Approve'], ['sequential', 'Sequential']].map(([v, l]) => (
                    <label key={v} className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border cursor-pointer text-xs font-medium transition-all ${editingGroup.routing_method === v ? 'border-primary bg-primary/5 text-primary' : 'hover:border-muted-foreground'}`}>
                      <input type="radio" checked={editingGroup.routing_method === v} onChange={() => setEditingGroup(p => ({...p, routing_method: v}))} className="h-3 w-3" />
                      {l}
                    </label>
                  ))}
                </div>
              </div>
              <div><Label className="text-xs">Members</Label>
                <div className="border rounded-lg max-h-48 overflow-y-auto mt-1">
                  {allUsers.map(u => {
                    const isMember = (editingGroup.members || []).some(m => (m.user_id || m) === u.user_id);
                    return (
                      <label key={u.user_id} className={`flex items-center gap-2 px-3 py-1.5 border-b last:border-0 cursor-pointer hover:bg-accent/30 text-sm ${isMember ? 'bg-primary/5' : ''}`}>
                        <input type="checkbox" checked={isMember} onChange={() => {
                          setEditingGroup(p => {
                            const members = p.members || [];
                            if (isMember) return {...p, members: members.filter(m => (m.user_id || m) !== u.user_id)};
                            return {...p, members: [...members, { user_id: u.user_id, name: u.name }]};
                          });
                        }} className="h-3.5 w-3.5" />
                        <span>{u.name}</span>
                        <span className="text-xs text-muted-foreground ml-auto">{u.user_id}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button size="sm" variant="outline" onClick={() => setEditingGroup(null)}>Cancel</Button>
                <Button size="sm" onClick={async () => {
                  try {
                    await api.put(`/api/admin/approval-groups/${editingGroup.group_code}`, {
                      description: editingGroup.description,
                      routing_method: editingGroup.routing_method,
                      members: editingGroup.members || [],
                    });
                    toast.success('Group updated');
                    setEditingGroup(null);
                    fetchAll();
                  } catch { toast.error('Update failed'); }
                }}>Save Group</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export function SetRuleApprovalPanel() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/api/admin/rule-approval-settings').then(r => setSettings(r.data.settings || {})).catch(() => setSettings({}));
    setLoading(false);
  }, []);

  const u = (k, v) => setSettings(p => ({ ...p, [k]: v }));
  const handleSave = async () => {
    setSaving(true);
    try { const { _id, id, type, ...payload } = settings; await api.put('/api/admin/rule-approval-settings', payload); toast.success('Rule approval settings saved'); }
    catch { toast.error('Save failed'); }
    setSaving(false);
  };

  if (loading || !settings) return <LoadingSpinner />;
  return (
    <div data-testid="set-rule-approval-panel">
      <h1 className="text-2xl font-bold mb-1">System Approval</h1>
      <p className="text-lg font-medium text-muted-foreground mb-4">Set Rule Approval</p>
      <p className="text-sm text-muted-foreground mb-6 border-b pb-4">
        Before proceeding with the processing of either a Purchase Order or Requisition, you can specify that the Order/Request obeys pre-defined Approval rules
        before it can be formerly approved/signed off by one or more nominated Users and/or Groups.
      </p>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Label className="w-56 text-sm">Activate Rule Approval</Label>
          <Switch checked={settings.activate_rule_approval} onCheckedChange={v => u('activate_rule_approval', v)} />
          <span className={`text-sm font-medium ${settings.activate_rule_approval ? 'text-green-600' : ''}`}>{settings.activate_rule_approval ? 'Yes' : 'No'}</span>
        </div>
        <div className="flex items-start gap-4">
          <Label className="w-56 text-sm pt-1">Delete Rule Approver(s)</Label>
          <Switch checked={settings.delete_rule_approvers} onCheckedChange={v => u('delete_rule_approvers', v)} />
          <span className={`text-sm font-medium ${settings.delete_rule_approvers ? 'text-green-600' : ''}`}>{settings.delete_rule_approvers ? 'Yes' : 'No'}</span>
          <p className="text-xs text-muted-foreground flex-1">This function will allow you to delete Approvers and choose the person to whom the order/request should be sent for approvals.</p>
        </div>
        <div className="flex items-start gap-4">
          <Label className="w-56 text-sm pt-1">Auto Populate Rule Approver(s)</Label>
          <Switch checked={settings.auto_populate_rule_approvers} onCheckedChange={v => u('auto_populate_rule_approvers', v)} />
          <span className={`text-sm font-medium ${settings.auto_populate_rule_approvers ? 'text-green-600' : ''}`}>{settings.auto_populate_rule_approvers ? 'Yes' : 'No'}</span>
          <p className="text-xs text-muted-foreground flex-1">When this function is checked, then the relevant Approvers will get automatically populated. If it is unchecked, then users will need to select the approvers manually.</p>
        </div>
      </div>
      <div className="flex justify-end mt-6 border-t pt-4"><Button onClick={handleSave} disabled={saving}><Save className="h-4 w-4 mr-1" />{saving ? 'Saving...' : 'Save'}</Button></div>
    </div>
  );
}

export function SetSystemApprovalRulesPanel() {
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const [rules, setRules] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingRule, setEditingRule] = useState(null); // null=list, 'new'=add, object=edit

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/api/admin/system-approval-rules', { params: { search } }); setRules(r.data.rules || []); setTotal(r.data.total || 0); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!await globalConfirm('Delete this rule?')) return;
    try { await api.delete(`/api/admin/system-approval-rules/${id}`); toast.success('Rule deleted'); load(); } catch { toast.error('Delete failed'); }
  };

  const handleReorder = async (newRules) => {
    setRules(newRules);
    try {
      await api.post('/api/admin/system-approval-rules/reorder', { rule_ids: newRules.map(r => r.id) });
      load(); // reload to get updated ranks
    } catch { toast.error('Reorder failed'); }
  };

  // If editing a rule, show the 3-tab editor
  if (editingRule) {
    return <ApprovalRuleEditor rule={editingRule === 'new' ? null : editingRule} onClose={() => { setEditingRule(null); load(); }} />;
  }

  return (
    <div data-testid="system-approval-rules-panel">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold">System Approval</h1>
        <span className="text-sm text-muted-foreground">Total Records: {total}</span>
      </div>
      <p className="text-lg font-medium text-muted-foreground mb-4">Set System Approval Rules</p>

      <div className="flex items-center gap-2 mb-4">
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search" className="w-40" />
        <Button size="sm" onClick={load}><Search className="h-3 w-3 mr-1" /> Search</Button>
        <Button size="sm" variant="outline" onClick={() => { setSearch(''); setTimeout(load, 100); }}><X className="h-3 w-3 mr-1" /> Clear</Button>
        <div className="flex-1" />
        <Button size="sm" onClick={() => setEditingRule('new')}><Plus className="h-3 w-3 mr-1" /> Add New</Button>
      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-muted/50 grid grid-cols-[40px_40px_1fr_1fr_80px_70px_60px_100px_36px] gap-0 px-2 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground border-b">
            <span></span><span>Rank</span><span>Rule Name</span><span>Description</span><span>Rule For</span><span className="text-center">Published</span><span className="text-center">Active</span><span>Last Updated</span><span></span>
          </div>
          {rules.length === 0 ? (
            <p className="px-3 py-6 text-center text-muted-foreground text-xs">No approval rules</p>
          ) : (
            <DragSortList
              items={rules}
              keyField="id"
              onReorder={handleReorder}
              renderItem={(r, i) => (
                <div
                  className={`grid grid-cols-[40px_1fr_1fr_80px_70px_60px_100px_36px] gap-0 items-center px-2 py-2 border-b cursor-pointer hover:bg-accent/30 ${r.published ? 'bg-green-50/50' : ''} ${r.active === false ? 'bg-red-50/50' : ''}`}
                  onClick={() => setEditingRule(r)}
                >
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                  <span className="text-sm text-primary font-medium truncate pr-2">{r.rule_name}</span>
                  <span className="text-sm text-muted-foreground truncate pr-2">{r.rule_description}</span>
                  <span className="text-sm">{r.rule_for}</span>
                  <span className="text-sm text-center">{r.published ? <span className="text-green-600 font-medium">Yes</span> : 'No'}</span>
                  <span className="text-sm text-center">{r.active !== false ? 'Yes' : <span className="text-red-500 font-medium">No</span>}</span>
                  <span className="text-[10px] text-muted-foreground">{r.updated_at ? new Date(r.updated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : ''}</span>
                  <span onClick={e => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" className="text-destructive h-6 w-6 p-0" onClick={() => handleDelete(r.id)}><Trash2 className="h-3 w-3" /></Button>
                  </span>
                </div>
              )}
            />
          )}
        </div>
      )}
    </div>
  );
}

export function ApprovalRuleEditor({ rule, onClose }) {
  const isNew = !rule;
  const { labels: expLabels } = useExpenditureLabels();
  const [tab, setTab] = useState('details');
  const [form, setForm] = useState({
    rule_name: rule?.rule_name || '', rule_description: rule?.rule_description || '',
    rule_for: rule?.rule_for || 'Request', active: rule?.active !== false, published: rule?.published || false,
    rank: rule?.rank || 1, approver_type: rule?.approver_type || 'Direct',
    routing_method: rule?.routing_method || 'sequential',
    criteria: rule?.criteria || [],
    approvers: (rule?.approvers || []).map(a => typeof a === 'string' ? a : (a.name || a.user_id || '')),
    approver_groups: rule?.approver_groups || [],
  });
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [conflicts, setConflicts] = useState(null);

  // Criteria builder state
  const [criteriaField, setCriteriaField] = useState('');
  const [criteriaOp, setCriteriaOp] = useState('>');
  const [criteriaValue, setCriteriaValue] = useState('');

  useEffect(() => {
    api.get('/api/admin/users', { params: { limit: 200 } }).then(r => setUsers((r.data.users || []).filter(u => u.is_licensed && !u.is_away))).catch(() => {});
    api.get('/api/admin/approval-groups').then(r => setGroups(r.data.groups || [])).catch(() => {});
  }, []);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const runConflictCheck = async () => {
    try {
      const r = await api.post('/api/admin/system-approval-rules/conflict-check', {
        rule: form, exclude_id: rule?.id || null,
      });
      setConflicts(r.data);
      return r.data;
    } catch { return { has_conflicts: false, conflicts: [], error_count: 0, warning_count: 0 }; }
  };

  const handleSave = async (publish) => {
    if (!form.rule_name) { toast.error('Rule name required'); return; }
    setSaving(true);
    // Run conflict check before saving
    const check = await runConflictCheck();
    if (check.error_count > 0) {
      const proceed = await globalConfirm(`This rule has ${check.error_count} conflict(s):\n\n${check.conflicts.filter(c => c.severity === 'error').map(c => '• ' + c.message).join('\n')}\n\nDo you want to save anyway?`);
      if (!proceed) { setSaving(false); return; }
    }
    try {
      const payload = { ...form };
      if (publish) payload.published = true;
      if (isNew) await api.post('/api/admin/system-approval-rules', payload);
      else await api.put(`/api/admin/system-approval-rules/${rule.id}`, payload);
      toast.success(publish ? 'Rule published' : (isNew ? 'Rule created' : 'Rule updated'));
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const addCriteria = () => {
    if (!criteriaField || !criteriaValue) { toast.error('Select field and value'); return; }
    const label = `${criteriaField} ${criteriaOp} ${criteriaValue}`;
    f('criteria', [...form.criteria, { field: criteriaField, operator: criteriaOp, value: criteriaValue, label }]);
    setCriteriaField(''); setCriteriaValue('');
  };

  const removeCriteria = (i) => f('criteria', form.criteria.filter((_, j) => j !== i));

  const toggleApprover = (name) => {
    const exists = form.approvers.includes(name);
    f('approvers', exists ? form.approvers.filter(a => a !== name) : [...form.approvers, name]);
  };

  const removeApprover = (i) => f('approvers', form.approvers.filter((_, j) => j !== i));

  const removeGroup = (i) => f('approver_groups', form.approver_groups.filter((_, j) => j !== i));

  const toggleGroup = (code) => {
    const exists = form.approver_groups.includes(code);
    f('approver_groups', exists ? form.approver_groups.filter(g => g !== code) : [...form.approver_groups, code]);
  };

  // Criteria field definitions with lookup endpoints — using soft labels from System Admin
  const valueLabel = form.rule_for === 'Order' ? 'Order Value' : 'Request Value';
  const CRITERIA_FIELDS = [
    { field: expLabels.exp_code_1 || 'Account', endpoint: '/api/account-codes/1', valueField: 'code', labelField: 'description' },
    { field: 'Buyer Name', endpoint: '/api/admin/users', valueField: 'user_id', labelField: 'first_name', isUser: true },
    { field: 'Category Code', endpoint: '/api/data/categories', valueField: 'code', labelField: 'description' },
    { field: expLabels.cost_centre || 'Cost Centre', endpoint: '/api/account-codes/7', valueField: 'code', labelField: 'description' },
    { field: 'Currency Code', endpoint: '/api/data/currencies', valueField: 'code', labelField: 'description' },
    { field: 'Delivery Point', endpoint: '/api/data/delivery-points', valueField: 'code', labelField: 'name' },
    { field: expLabels.exp_code_3 || 'Department Code', endpoint: '/api/account-codes/3', valueField: 'code', labelField: 'description' },
    { field: 'Document Type', endpoint: '/api/data/document-types', valueField: 'code', labelField: 'description' },
    { field: expLabels.exp_code_4 || 'GL', endpoint: '/api/account-codes/4', valueField: 'code', labelField: 'description' },
    { field: 'Invoice Point', endpoint: '/api/data/invoice-points', valueField: 'code', labelField: 'description' },
    { field: expLabels.exp_code_6 || 'Nominal Code', endpoint: '/api/account-codes/6', valueField: 'code', labelField: 'description' },
    { field: expLabels.exp_code_5 || 'Property Code', endpoint: '/api/account-codes/5', valueField: 'code', labelField: 'description' },
    { field: valueLabel, numeric: true },
    { field: expLabels.exp_code_2 || 'Restaurant', endpoint: '/api/account-codes/2', valueField: 'code', labelField: 'description' },
    { field: 'Supplier', endpoint: '/api/data/suppliers', valueField: 'supplier_code', labelField: 'supplier_name' },
  ];
  const OPS = ['>', '<', '>=', '<=', '=', '!=', 'contains', 'starts with'];
  const selectedFieldDef = CRITERIA_FIELDS.find(f2 => f2.field === criteriaField);

  return (
    <div data-testid="approval-rule-editor">
      <h1 className="text-2xl font-bold mb-1">Approval</h1>
      <p className="text-sm text-muted-foreground mb-4">Set System Approval Rules</p>

      {/* 3-tab navigation */}
      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="details" className="gap-1"><FileText className="h-3.5 w-3.5" /> Rule Details</TabsTrigger>
          <TabsTrigger value="criteria" className="gap-1"><Search className="h-3.5 w-3.5" /> Select Criteria</TabsTrigger>
          <TabsTrigger value="approver" className="gap-1"><Users className="h-3.5 w-3.5" /> Rule Approver</TabsTrigger>
        </TabsList>

        {/* Tab 1: Rule Details */}
        <TabsContent value="details">
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2 space-y-4">
              <div className="space-y-1.5"><Label>Rule Name</Label><Input value={form.rule_name} onChange={e => f('rule_name', e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Rule Description</Label><Input value={form.rule_description} onChange={e => f('rule_description', e.target.value)} /></div>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center gap-2"><Label>Active</Label><Switch checked={form.active} onCheckedChange={v => f('active', v)} /><span className={`text-sm ${form.active ? 'text-green-600 font-medium' : ''}`}>{form.active ? 'Yes' : 'No'}</span></div>
                <div className="space-y-1"><Label>Rank</Label><Input type="number" value={form.rank} onChange={e => f('rank', parseInt(e.target.value) || 1)} className="w-20" /></div>
                <div className="flex items-center gap-2"><Label>Published</Label><span className={`text-sm ${form.published ? 'text-green-600 font-medium' : ''}`}>{form.published ? 'Yes' : 'No'}</span></div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="border rounded-lg p-4">
                <h4 className="text-xs font-semibold mb-3 text-muted-foreground">Rule For</h4>
                <div className="space-y-2">
                  {['Request', 'Order', 'RFQ'].map(v => (
                    <label key={v} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${form.rule_for === v ? 'border-primary bg-primary/5' : 'border-muted'}`}>
                      <input type="radio" name="rule_for" value={v} checked={form.rule_for === v} onChange={() => f('rule_for', v)} className="h-4 w-4" />
                      <span className="text-sm">{v}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="border rounded-lg p-4">
                <h4 className="text-xs font-semibold mb-3 text-muted-foreground">Approver Type</h4>
                <div className="space-y-2">
                  {['Direct', 'As per Approval Limit'].map(v => (
                    <label key={v} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${form.approver_type === v ? 'border-primary bg-primary/5' : 'border-muted'}`}>
                      <input type="radio" name="approver_type" value={v} checked={form.approver_type === v} onChange={() => f('approver_type', v)} className="h-4 w-4" />
                      <span className="text-sm">{v}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
          {form.published && <p className="text-xs text-orange-600 mt-4">Current rule is used in your system. You can't change Criteria.</p>}
          <p className="text-xs text-muted-foreground mt-2">Note: If you modify the rule, you have to first update it and then publish it again for it to reflect in your changes.</p>
        </TabsContent>

        {/* Tab 2: Select Criteria */}
        <TabsContent value="criteria">
          <div className="grid grid-cols-2 gap-6">
            <div className="border rounded-lg">
              <div className="px-4 py-2 bg-muted/30 flex items-center gap-2"><Search className="h-3.5 w-3.5 text-muted-foreground" /><span className="text-sm font-semibold">Select Criteria</span></div>
              <div className="max-h-[400px] overflow-y-auto">
                {CRITERIA_FIELDS.map(fd => (
                  <button key={fd.field} className={`w-full text-left px-4 py-2.5 text-sm border-b hover:bg-accent ${criteriaField === fd.field ? 'bg-primary/5 text-primary font-medium' : ''}`}
                    onClick={() => { setCriteriaField(fd.field); setCriteriaValue(''); }}>{fd.field}</button>
                ))}
              </div>
              {criteriaField && (
                <div className="p-3 border-t bg-muted/10 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium w-24 truncate">{criteriaField}</span>
                    <select value={criteriaOp} onChange={e => setCriteriaOp(e.target.value)} className="h-7 w-16 rounded border border-input bg-background px-1 text-xs">
                      {OPS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedFieldDef?.numeric ? (
                      <Input value={criteriaValue} onChange={e => setCriteriaValue(e.target.value)} className="h-8 flex-1 text-xs" placeholder="Enter value..." type="number" />
                    ) : selectedFieldDef?.endpoint ? (
                      <div className="flex-1">
                        <TypeAheadLookup
                          endpoint={selectedFieldDef.endpoint}
                          valueField={selectedFieldDef.valueField}
                          labelField={selectedFieldDef.labelField}
                          value={criteriaValue}
                          displayValue={criteriaValue}
                          placeholder={`Search ${criteriaField}...`}
                          onChange={(code) => setCriteriaValue(code)}
                        />
                      </div>
                    ) : (
                      <Input value={criteriaValue} onChange={e => setCriteriaValue(e.target.value)} className="h-8 flex-1 text-xs" placeholder="Enter value..." />
                    )}
                    <Button size="sm" className="h-8" onClick={addCriteria}><Plus className="h-3 w-3 mr-1" /> Add</Button>
                  </div>
                </div>
              )}
            </div>
            <div className="border rounded-lg">
              <div className="px-4 py-2 bg-muted/30 flex items-center justify-between"><span className="text-sm font-semibold">Selected Criteria</span>
                {form.criteria.length > 0 && <Button size="sm" variant="ghost" className="h-6 text-destructive" onClick={() => f('criteria', [])}><Trash2 className="h-3 w-3" /></Button>}</div>
              <div className="min-h-[200px]">
                {form.criteria.length === 0 ? <p className="px-4 py-8 text-center text-xs text-muted-foreground">No criteria selected. Click a field on the left to add.</p> :
                  form.criteria.map((c, i) => (
                    <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b hover:bg-accent/30">
                      <span className="text-sm">{c.label}</span>
                      <Button size="sm" variant="ghost" className="h-6 text-destructive" onClick={() => removeCriteria(i)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Tab 3: Rule Approver */}
        <TabsContent value="approver">
          {/* Routing Method */}
          <div className="mb-4 border rounded-lg p-4">
            <h4 className="text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wide">Notification Routing Method</h4>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'sequential', label: 'Sequential', desc: 'Approvers are notified one after another in order. Each must approve before the next is notified.' },
                { value: 'parallel', label: 'All at Once', desc: 'All approvers are notified simultaneously. All must approve for the document to be approved.' },
                { value: 'any_one', label: 'Any One', desc: 'All approvers are notified simultaneously. Only one approval is needed.' },
              ].map(m => (
                <label key={m.value} className={`flex flex-col p-3 rounded-lg border cursor-pointer transition-all ${form.routing_method === m.value ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-muted hover:border-muted-foreground'}`}>
                  <div className="flex items-center gap-2">
                    <input type="radio" name="routing_method" value={m.value} checked={form.routing_method === m.value} onChange={() => f('routing_method', m.value)} className="h-4 w-4" />
                    <span className="text-sm font-semibold">{m.label}</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1 ml-6">{m.desc}</p>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2 border rounded-lg">
              <Tabs defaultValue="single">
                <TabsList className="w-full rounded-none border-b">
                  <TabsTrigger value="single" className="flex-1 gap-1"><Users className="h-3.5 w-3.5 text-primary" /> Single Approver</TabsTrigger>
                  <TabsTrigger value="group" className="flex-1 gap-1"><Plus className="h-3.5 w-3.5 text-green-600" /> Group Approver</TabsTrigger>
                </TabsList>
                <TabsContent value="single" className="p-0">
                  <div className="max-h-[300px] overflow-y-auto">
                    {users.map(u => {
                      const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
                      return (
                        <button key={u.user_id || u._id} className={`w-full text-left px-4 py-2.5 text-sm border-b hover:bg-accent ${form.approvers.includes(name) ? 'bg-primary/5' : ''}`}
                          onClick={() => toggleApprover(name)}>{name}</button>
                      );
                    })}
                  </div>
                  <p className="px-4 py-2 text-xs text-muted-foreground border-t">Note: Users who are away or do not have a license will not show up as Single Approvers.</p>
                </TabsContent>
                <TabsContent value="group" className="p-0">
                  <div className="max-h-[300px] overflow-y-auto">
                    {groups.filter(g => g.active !== false).map(g => (
                      <button key={g.group_code} className={`w-full text-left px-4 py-2.5 text-sm border-b hover:bg-accent ${form.approver_groups.includes(g.group_code) ? 'bg-green-50' : ''}`}
                        onClick={() => toggleGroup(g.group_code)}>{g.group_code} — {g.description}</button>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            {/* Selected Approvers Panel — with delete & drag reorder */}
            <div className="border rounded-lg">
              <div className="px-4 py-2 bg-muted/30 border-b flex items-center justify-between">
                <span className="text-sm font-semibold">Selected Approvers</span>
                {form.routing_method === 'sequential' && form.approvers.length > 1 && (
                  <span className="text-[10px] text-muted-foreground">Drag to reorder</span>
                )}
              </div>
              <div className="max-h-[350px] overflow-y-auto p-1">
                {form.approvers.length === 0 && form.approver_groups.length === 0 && (
                  <p className="px-4 py-8 text-center text-xs text-muted-foreground">No approvers selected</p>
                )}
                {form.routing_method === 'sequential' && form.approvers.length > 0 ? (
                  <DragSortList
                    items={form.approvers}
                    keyField={null}
                    onReorder={(newOrder) => f('approvers', newOrder)}
                    renderItem={(a, i) => (
                      <div className="flex items-center gap-2 py-1.5 border-b">
                        <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                        <Users className="h-3 w-3 text-primary flex-shrink-0" />
                        <span className="text-sm flex-1 truncate">{a}</span>
                        <button onClick={() => removeApprover(i)} className="text-destructive hover:opacity-70 flex-shrink-0" title="Remove"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    )}
                  />
                ) : (
                  form.approvers.map((a, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 border-b hover:bg-accent/30 group">
                      <Users className="h-3 w-3 text-primary flex-shrink-0" />
                      <span className="text-sm flex-1 truncate">{a}</span>
                      <button onClick={() => removeApprover(i)} className="text-destructive opacity-0 group-hover:opacity-100 transition-opacity hover:opacity-70 flex-shrink-0" title="Remove"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))
                )}
                {form.approver_groups.map((g, i) => (
                  <div key={`g-${i}`} className="flex items-center gap-2 px-3 py-2 border-b hover:bg-accent/30 group">
                    <Plus className="h-3 w-3 text-green-600 flex-shrink-0" />
                    <span className="text-sm flex-1 truncate">{g}</span>
                    <button onClick={() => removeGroup(i)} className="text-destructive opacity-0 group-hover:opacity-100 transition-opacity hover:opacity-70 flex-shrink-0" title="Remove"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Conflict Check Results */}
      {conflicts && conflicts.has_conflicts && (
        <div className="border rounded-lg p-4 mb-4 space-y-2">
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
            Conflict Check: {conflicts.error_count} error(s), {conflicts.warning_count} warning(s)
          </p>
          {conflicts.conflicts.map((c, i) => (
            <div key={i} className={`flex items-start gap-2 px-3 py-2 rounded-lg text-xs ${c.severity === 'error' ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-amber-50 border border-amber-200 text-amber-700'}`}>
              <span className="font-bold flex-shrink-0">{c.severity === 'error' ? 'ERROR' : 'WARNING'}</span>
              <span>{c.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Footer buttons */}
      <div className="flex justify-between border-t pt-4">
        <Button variant="outline" size="sm" onClick={runConflictCheck} data-testid="check-conflicts-btn">
          <Search className="h-3.5 w-3.5 mr-1" /> Check Conflicts
        </Button>
        <div className="flex gap-2">
          <Button onClick={() => handleSave(true)} disabled={saving} variant="default"><Save className="h-4 w-4 mr-1" /> Publish Rule</Button>
          <Button onClick={() => handleSave(false)} disabled={saving} variant="secondary"><Edit className="h-4 w-4 mr-1" /> {isNew ? 'Create Rule' : 'Update Rule'}</Button>
          <Button variant="outline" onClick={onClose}><X className="h-4 w-4 mr-1" /> Back</Button>
        </div>
      </div>
    </div>
  );
}

export function SetRuleApprovalUserGroupPanel() {
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newGroup, setNewGroup] = useState({ group_name: '', group_description: '', active: true });

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/api/admin/rule-approval-user-groups', { params: { search } }); setGroups(r.data.groups || []); setTotal(r.data.total || 0); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!newGroup.group_name) { toast.error('Group name required'); return; }
    try { await api.post('/api/admin/rule-approval-user-groups', newGroup); toast.success('User group created'); setNewGroup({ group_name: '', group_description: '', active: true }); setShowAdd(false); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Create failed'); }
  };
  const handleDelete = async (id) => {
    if (!await globalConfirm('Delete this user group?')) return;
    try { await api.delete(`/api/admin/rule-approval-user-groups/${id}`); toast.success('User group deleted'); load(); } catch { toast.error('Delete failed'); }
  };

  return (
    <div data-testid="rule-approval-user-group-panel">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold">System Approval</h1>
        <span className="text-sm text-muted-foreground">Total Records: {total}</span>
      </div>
      <p className="text-lg font-medium text-muted-foreground mb-4">Set Rule Approval - User Group</p>
      <p className="text-sm text-muted-foreground mb-4">Note: Create/Edit User Groups for System Approval Rule.</p>

      <div className="flex items-center gap-2 mb-4">
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search" className="w-40" />
        <Button size="sm" onClick={load}><Search className="h-3 w-3 mr-1" /> Search</Button>
        <Button size="sm" variant="outline" onClick={() => { setSearch(''); setTimeout(load, 100); }}><X className="h-3 w-3 mr-1" /> Clear</Button>
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={() => setShowAdd(true)}><Plus className="h-3 w-3 mr-1" /> Add New</Button>
      </div>

      {showAdd && (
        <div className="border rounded-lg p-4 mb-4 grid grid-cols-3 gap-3 items-end bg-muted/10">
          <div className="space-y-1"><Label className="text-xs">Group Name *</Label><Input value={newGroup.group_name} onChange={e => setNewGroup(p => ({ ...p, group_name: e.target.value }))} /></div>
          <div className="space-y-1"><Label className="text-xs">Group Description</Label><Input value={newGroup.group_description} onChange={e => setNewGroup(p => ({ ...p, group_description: e.target.value }))} /></div>
          <div className="flex gap-2"><Button size="sm" onClick={handleCreate}>Create</Button><Button size="sm" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button></div>
        </div>
      )}

      {loading ? <LoadingSpinner /> : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr><th className="text-left px-3 py-2 font-medium">Group Name</th><th className="text-left px-3 py-2 font-medium">Group Description</th><th className="text-center px-3 py-2 font-medium">Active</th><th className="w-10"></th></tr>
            </thead>
            <tbody>
              {groups.length === 0 ? <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground text-xs">No user groups</td></tr> :
                groups.map(g => (
                  <tr key={g.id} className="border-t hover:bg-accent/30">
                    <td className="px-3 py-2">{g.group_name}</td>
                    <td className="px-3 py-2 text-muted-foreground">{g.group_description}</td>
                    <td className="px-3 py-2 text-center">{g.active ? 'Yes' : 'No'}</td>
                    <td className="px-2"><Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(g.id)}><Trash2 className="h-3 w-3" /></Button></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
